import { Upload, Typography, message } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Dragger } = Upload;

const MAX_FILE_SIZE_MB = 25;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
const DEFAULT_MAX_COUNT = 5;

interface EvidenceUploadProps {
  fileList: UploadFile[];
  onChange: (info: Parameters<NonNullable<UploadProps['onChange']>>[0]) => void;
  maxCount?: number;
}

const EvidenceUpload = ({ fileList, onChange, maxCount = DEFAULT_MAX_COUNT }: EvidenceUploadProps) => {
  const { t } = useTranslation();

  const beforeUpload = (file: File): boolean | typeof Upload.LIST_IGNORE => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      message.error(
        String(t('evidence_upload_invalid_type', { defaultValue: 'Only JPEG and PNG files are allowed.' })),
      );
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      message.error(
        String(t('evidence_upload_too_large', {
          defaultValue: `File must be smaller than ${MAX_FILE_SIZE_MB}MB.`,
          maxMB: MAX_FILE_SIZE_MB,
        })),
      );
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleChange: UploadProps['onChange'] = (info) => {
    const filtered = info.fileList.filter((f) => {
      if (!f.originFileObj) return true;
      const isValidType = ALLOWED_TYPES.includes(f.originFileObj.type);
      const isValidSize = f.originFileObj.size <= MAX_FILE_SIZE_BYTES;
      return isValidType && isValidSize;
    });
    onChange({ ...info, fileList: filtered.slice(0, maxCount) });
  };

  return (
    <div className="flex flex-col gap-2">
      <Dragger
        listType="picture"
        fileList={fileList}
        onChange={handleChange}
        beforeUpload={beforeUpload}
        multiple
        maxCount={maxCount}
        accept=".jpg,.jpeg,.png,.mp4,.mov,.avi,.webm"
        customRequest={({ onSuccess }) => {
          if (onSuccess) onSuccess('ok');
        }}
        style={{ padding: 0 }}
      >
        <div className="flex flex-col items-center gap-1 py-3">
          <InboxOutlined style={{ fontSize: 24, color: '#999' }} />
          <span className="text-[12px] text-[rgb(57,57,60)]">
            {String(t('evidence_upload_drag_text', { defaultValue: 'Click or drag files to upload' }))}
          </span>
          <Typography.Text type="secondary" className="text-[11px]">
            {String(
              t('evidence_upload_hint', {
                defaultValue: `Images & videos, max ${MAX_FILE_SIZE_MB}MB per file, up to ${maxCount} files.`,
                maxMB: MAX_FILE_SIZE_MB,
                maxCount,
              }),
            )}
          </Typography.Text>
        </div>
      </Dragger>
    </div>
  );
};

export default EvidenceUpload;
